/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Delays the execution of the given function by the "delay"
 * can also be used for async functions
 * @param callback
 * @param delay
 */

export function debounce<T extends (...args: Array<any>) => any>(
  callback: T,
  delay: number,
): (...args: Parameters<T>) => Promise<ReturnType<T>> | void {
  let timerId: ReturnType<typeof setTimeout> | null;
  let savedArgs: Parameters<T> | null;

  function debounced(this: any, ...params: Parameters<T>): Promise<ReturnType<T>> | void {
    savedArgs = params;

    return new Promise(resolve => {
      // eslint-disable-next-line @stencil-community/strict-boolean-conditions
      if (timerId) {
        clearTimeout(timerId);
      }

      timerId = setTimeout(async () => {
        timerId = null;
        // eslint-disable-next-line @stencil-community/strict-boolean-conditions
        if (savedArgs) {
          const result = await callback.apply(this, savedArgs);
          resolve(result);
          savedArgs = null;
        }
      }, delay);
    });
  }

  return debounced;
}

let incrementedNumber = 0;

export function getIncrementedNumber(): number {
  return ++incrementedNumber;
}

/**
 * Returns the offset of the given element in relation to the body.
 * @param element
 */
export function getOffset(element: HTMLElement): { left: number; top: number } {
  const rect = element.getBoundingClientRect();
  return {
    left: rect.left + window.scrollX,
    top: rect.top + window.scrollY,
  };
}

/**
 * Interpolate converts a string to a literal and replaces variable names with variable values
 * ```
 *      const dados = {nome: 'Marquinhos', email: 'xiii@marquinhos.com'};
 *      template('${nome} utiliza esse email: ${email}!', dados)
 * ```
 * @param templateString - string to replace
 * @param templateVars - current variables
 */
export function interpolate(templateString: string, templateVars: Record<string, any>): string {
  return templateString.replace(/\${(\w+)}/g, (fullMatch, key) => {
    const value = templateVars[key];

    // Use fullMatch for fallback if value is undefined or null
    if (value === undefined || value === null) {
      return fullMatch; // fallback to original placeholder
    }
    return String(value);
  });
}

export function stringMatchesByRule(string: string, rule: string, flags?: string) {
  return !!string.match(new RegExp(rule, flags));
}
